import { CLOSED_DEAL_STAGES } from '../../lib/stages'
import type { PipelineDeal } from './usePipeline'

/**
 * Pure helpers for the Deals board. Each one mirrors a piece of the legacy
 * pipeline code in crm/index.html (`fmt`, `fmtDate`, `renderDealStats`,
 * the search filter inside `renderKanban`) so the island prints the same
 * numbers the legacy board did.
 */

/** Legacy `fmt()`: $12k from 1000 up, $950 below. */
export function formatMoney(v: number): string {
  return v >= 1000 ? '$' + (v / 1000).toFixed(0) + 'k' : '$' + v.toFixed(0)
}

/** Legacy `fmtDate()`: "Nov 1, 2026". */
export function formatCloseDate(d: string): string {
  return new Date(d).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

/**
 * How far a close date is from today, for an open deal's card:
 * "Closes today", "Closes in 9 days", "3 days overdue". Closed deals and
 * dates more than a month out fall back to the plain date.
 */
export function closeHint(
  d: string,
  stage: string,
  now: Date = new Date(),
): { text: string; overdue: boolean } {
  const date = new Date(d)
  if (CLOSED_DEAL_STAGES.includes(stage)) return { text: formatCloseDate(d), overdue: false }
  const startOf = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime()
  // Date-only strings parse as UTC midnight; read their calendar day in UTC.
  const target = /^\d{4}-\d{2}-\d{2}$/.test(d)
    ? new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()).getTime()
    : startOf(date)
  const days = Math.round((target - startOf(now)) / 86400000)
  if (days === 0) return { text: 'Closes today', overdue: false }
  if (days === 1) return { text: 'Closes tomorrow', overdue: false }
  if (days < 0) return { text: `${-days} day${days === -1 ? '' : 's'} overdue`, overdue: true }
  if (days <= 30) return { text: `Closes in ${days} days`, overdue: false }
  return { text: formatCloseDate(d), overdue: false }
}

/** The company a card shows: the linked account first, then the contact's company. */
export function dealCompany(d: PipelineDeal): string {
  return d.accounts?.company_name || d.contacts?.company_name || ''
}

export interface PipelineStats {
  total: number
  pipelineValue: number
  avgDealSize: number
  won: number
  closeRate: number
}

/**
 * Legacy `renderDealStats()`. Pipeline value counts open deals only; the
 * average divides that by the open-deal count; close rate is won / all.
 * Legacy labelled the won count "Won (30d)" but never filtered by date; the
 * island shows the same all-time count labelled "Won".
 */
export function computeStats(deals: PipelineDeal[]): PipelineStats {
  const active = deals.filter((d) => !CLOSED_DEAL_STAGES.includes(d.stage))
  const won = deals.filter((d) => d.stage === 'Closed Won').length
  const total = deals.length
  const pipelineValue = active.reduce((s, d) => s + (Number(d.value) || 0), 0)
  const avgDealSize = total ? pipelineValue / Math.max(active.length, 1) : 0
  const closeRate = total ? Math.round((won / total) * 100) : 0
  return { total, pipelineValue, avgDealSize, won, closeRate }
}

/** Legacy search: title, contact name, or company, case-insensitive. */
export function matchesSearch(d: PipelineDeal, search: string): boolean {
  const q = search.trim().toLowerCase()
  if (!q) return true
  return (
    (d.title || '').toLowerCase().includes(q) ||
    (d.contacts?.full_name || '').toLowerCase().includes(q) ||
    dealCompany(d).toLowerCase().includes(q)
  )
}

/** Sum of `value` across a column. */
export function columnTotal(deals: PipelineDeal[]): number {
  return deals.reduce((s, d) => s + (Number(d.value) || 0), 0)
}
